/**
 * ApprovalControls · Mar 1, 2026
 * Reusable per-document approval widget (Badge + Approve / Reject / Unapprove).
 *
 * Renders:
 *   - A status badge (Draft / Authorized / Rejected).
 *   - Three action buttons — visible & clickable ONLY when the current
 *     user's role is Administrator / Manager / Management.
 *   - Inline rejection-reason prompt (mandatory, ≥ 4 chars).
 *   - Amber warning banner listing downstream blockers when Unapprove
 *     is rejected by the backend.
 *
 * The parent component owns the doc object; on any successful action
 * we call `onChange(response)` so the parent can refresh from
 * `GET /approvals/doc/{type}/{id}` (or just re-fetch its detail).
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getUser } from "@/lib/secureStorage";
import {
  CheckCircleIcon, ProhibitIcon, ArrowCounterClockwiseIcon, WarningIcon,
} from "@phosphor-icons/react";
import { authStatusLabel } from "@/lib/authTerminology";

const APPROVED_VALUES = new Set(["Authorized", "Approved"]);

// Sep 04, 2026 · Manager-tier bypass — mirrors backend `_APPROVER_ROLES`.
// Any user carrying one of these four roles sees Authorise / Reject /
// Un-authorise without needing a per-module `<mod>:approve` grant.
// Every other role falls through to the RBAC grant check.
const APPROVER_ROLES = new Set([
  "Administrator",
  "General Manager",
  "Manager",
  "Production Manager",
]);

const STATUS_STYLE = {
  Authorized: "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  Approved:   "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  Rejected:   "border-red-500/50 text-red-300 bg-red-500/10",
  "Pending Approval": "border-sky-500/50 text-sky-300 bg-sky-500/10",
  Draft:      "border-zinc-500/40 text-zinc-300",
};

// Aug 31, 2026 · Zero-Trust — approvals gate delegates to DB grants.
// docType (`pi`, `so`, `wo`, etc.) maps to the sidebar-sync module code
// so `<module>:approve` (and `<module>:unapprove`) grants control the
// three action buttons here. Administrator always passes.
const DOC_TO_MODULE = {
  pi: "proforma_invoices",
  epi: "export_proforma_invoices",
  so: "sales_orders",
  eso: "exports",
  wo: "production_orders",
  po: "purchase_orders",
  gst: "gst_invoices",
  egst: "export_gst_invoices",
};

export function ApprovalBadge({ status, reason, size = "md", testId }) {
  const styleKey = APPROVED_VALUES.has(status) ? "Authorized" : (status || "Draft");
  const label = authStatusLabel(status);
  const cls = STATUS_STYLE[styleKey] || STATUS_STYLE.Draft;
  const pad = size === "sm" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span data-testid={testId || "approval-badge"} title={reason ? `Reason: ${reason}` : label}
      className={`inline-flex items-center rounded-sm border font-mono uppercase tracking-widest ${pad} ${cls}`}>
      {label}
    </span>
  );
}

export default function ApprovalControls({
  docType, docId, docNo,
  approvalStatus, rejectionReason,
  approvalHistory = [],
  onChange,
  compact = false,
  testIdPrefix,
}) {
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const user = getUser();
  const { hasPerm } = useAuth();
  const _module = DOC_TO_MODULE[docType] || docType;
  const canManage = APPROVER_ROLES.has(user?.role) || !!hasPerm?.(`${_module}:approve`);
  const tid = testIdPrefix || `approval-${docType}`;

  const status = APPROVED_VALUES.has(approvalStatus) ? "Authorized" : (approvalStatus || "Draft");
  const isAuthorized = status === "Authorized";
  const isRejected   = status === "Rejected";

  const call = async (path, body) => {
    setBusy(true); setBlockers([]);
    try {
      const { data } = await api.post(`/approvals/doc/${docType}/${docId}/${path}`, body || {});
      onChange && await onChange(data);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail || `${path} failed`;
      // Backend blockers arrive as "Cannot unapprove — downstream activity exists: A; B; C"
      const idx = String(detail).indexOf("downstream activity exists:");
      if (idx >= 0) {
        setBlockers(detail.slice(idx + "downstream activity exists:".length).split(";").map(s => s.trim()).filter(Boolean));
      }
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!window.confirm(`Authorise ${docNo || `this ${docType.toUpperCase()}`}?\n\nThis locks the record from further editing.`)) return;
    const d = await call("authorize");
    if (d) toast.success(`${d.doc_no || docNo} · Authorised`);
  };
  const reject = async () => {
    const reason = window.prompt(`Reject ${docNo || `this ${docType.toUpperCase()}`}?\n\nEnter a reason (min 4 chars). The creator will see this and be able to resubmit.`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason must be ≥ 4 characters"); return; }
    const d = await call("reject", { reason: reason.trim() });
    if (d) toast.success(`${d.doc_no || docNo} · Rejected`);
  };
  const unapprove = async () => {
    const isWo = docType === "wo";
    const reason = window.prompt(`Un-authorise ${docNo || `this ${docType.toUpperCase()}`}?\n\nReverts to Draft and re-unlocks editing.\nBlocked automatically if downstream docs / production entries exist.\n\n${isWo ? "Reason required" : "Optional reason"} (kept in audit log):`);
    if (reason === null) return;
    if (isWo && !reason.trim()) { toast.error("Reason required"); return; }
    const d = await call("unapprove", { reason: reason.trim() });
    if (d) toast.success(`${d.doc_no || docNo} · Reverted to Draft`);
  };

  const containerCls = compact
    ? "flex items-center gap-2 flex-wrap"
    : "border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 flex items-center gap-2 flex-wrap";

  return (
    <div className={containerCls} data-testid={`${tid}-panel`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Authorisation</div>
      <ApprovalBadge status={status} reason={rejectionReason} testId={`${tid}-badge`} />
      {isRejected && rejectionReason && (
        <span className="text-[11px] text-red-300 italic" data-testid={`${tid}-reason`}>· {rejectionReason}</span>
      )}
      {canManage && (
        <div className="ml-auto flex items-center gap-2">
          {!isAuthorized && (
            <button data-testid={`${tid}-approve-btn`} disabled={busy} onClick={approve}
              className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5">
              <CheckCircleIcon size={12} weight="bold" /> Authorise
            </button>
          )}
          {!isRejected && (
            <button data-testid={`${tid}-reject-btn`} disabled={busy} onClick={reject}
              className="h-8 px-3 border border-red-500/50 text-red-300 hover:bg-red-500/10 text-[11px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5">
              <ProhibitIcon size={12} weight="bold" /> Reject
            </button>
          )}
          {isAuthorized && (
            <button data-testid={`${tid}-unapprove-btn`} disabled={busy} onClick={unapprove}
              className="h-8 px-3 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[11px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5">
              <ArrowCounterClockwiseIcon size={12} weight="bold" /> Un-authorise
            </button>
          )}
        </div>
      )}
      {blockers.length > 0 && (
        <div data-testid={`${tid}-blockers`} className="w-full mt-2 border border-amber-500/40 bg-amber-500/5 rounded-sm p-2 text-[11px] text-amber-200 flex items-start gap-2">
          <WarningIcon size={13} weight="bold" className="mt-0.5" />
          <div>
            <b>Cannot un-authorise — downstream activity exists:</b>
            <ul className="list-disc list-inside mt-1">
              {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        </div>
      )}
      {(approvalHistory || []).length > 0 && !compact && (
        <details className="w-full mt-1">
          <summary className="text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-300">
            Approval history ({approvalHistory.length})
          </summary>
          <div className="mt-1 space-y-0.5">
            {approvalHistory.slice().reverse().map((h, i) => (
              <div key={i} className="text-[11px] text-zinc-400 font-mono">
                <span className="text-zinc-500">{(h.at || "").slice(0, 19).replace("T", " ")}</span>
                {" · "}<span className={h.action === "Authorized" ? "text-emerald-300" : h.action === "Rejected" ? "text-red-300" : "text-amber-300"}>{h.action}</span>
                {" · "}<span>{h.by}</span>
                {h.reason ? <span className="text-zinc-300 italic"> — {h.reason}</span> : null}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
